import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";

import { LandingPage } from "./LandingPage";
import type { LayoutScene } from "./layout-presets";
import { PrivacyPolicyPage } from "./PrivacyPolicyPage";
import { TermsOfUsePage } from "./TermsOfUsePage";

import "./styles.css";

const LayoutEditor = import.meta.env.DEV
  ? lazy(async () => {
      await import("./layout-editor.css");
      const module = await import("./LayoutEditor");

      return { default: module.LayoutEditor };
    })
  : undefined;

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Could not mount Landing Page: #root was not found.");
}

const pathname = window.location.pathname.replace(/\/$/, "");
const searchParams = new URLSearchParams(window.location.search);
const isPrivacyPolicyPage = pathname === "/privacy";
const isTermsOfUsePage = pathname === "/terms";
const isLayoutEditor =
  import.meta.env.DEV && pathname === "" && searchParams.get("layoutEditor") === "1";
const isLayoutPreview = pathname === "" && searchParams.get("layoutPreview") === "1";
const requestedLayoutScene = searchParams.get("layoutScene");
const requestedMobileStore = searchParams.get("mobileStore");
const layoutScene: LayoutScene =
  requestedLayoutScene === "packet" ||
  requestedLayoutScene === "packs" ||
  requestedLayoutScene === "garden"
    ? requestedLayoutScene
    : "packs";

document.title = isPrivacyPolicyPage
  ? "Privacy Policy | Sky Garden"
  : isTermsOfUsePage
    ? "Terms of Use | Sky Garden"
    : isLayoutEditor
      ? "Layout Editor | Sky Garden"
      : "Sky Garden";

createRoot(rootElement).render(
  <StrictMode>
    {isPrivacyPolicyPage ? (
      <PrivacyPolicyPage />
    ) : isTermsOfUsePage ? (
      <TermsOfUsePage />
    ) : isLayoutEditor && LayoutEditor ? (
      <Suspense fallback={null}>
        <LayoutEditor />
      </Suspense>
    ) : (
      <LandingPage
        editorScene={isLayoutPreview ? layoutScene : undefined}
        isLayoutPreview={isLayoutPreview}
        mobileStoreOverride={
          isLayoutPreview && (requestedMobileStore === "app" || requestedMobileStore === "play")
            ? requestedMobileStore
            : undefined
        }
      />
    )}
  </StrictMode>,
);
